/**
 * TeamsPage - View and manage teams
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { teamsAPI } from '../services/api';
import toast from 'react-hot-toast';

const TeamsPage = () => {
  const navigate = useNavigate();
  const { user, currentTeam, switchTeam } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const data = await teamsAPI.getMyTeams();
      setTeams(data);
    } catch (error) {
      console.error('Error loading teams:', error);
      toast.error('Failed to load teams');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!newTeamName.trim()) {
      toast.error('Please enter a team name');
      return;
    }

    try {
      setCreating(true);
      const newTeam = await teamsAPI.createTeam(newTeamName);
      toast.success(`Team "${newTeam.name}" created successfully!`);
      setShowCreateModal(false);
      setNewTeamName('');
      await loadTeams();

      // Navigate to the new team's dashboard
      navigate(`/teams/${newTeam.id}`);
    } catch (error) {
      console.error('Error creating team:', error);
      toast.error(error.response?.data?.detail || 'Failed to create team');
    } finally {
      setCreating(false);
    }
  };

  const handleSwitchTeam = (team) => {
    switchTeam(team.id);
    navigate(`/teams/${team.id}`);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading teams...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>My Teams</h1>
          <p style={styles.subtitle}>Manage your volleyball teams and rosters</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          style={styles.createButton}
        >
          + Create Team
        </button>
      </div>

      {teams.length === 0 ? (
        <div style={styles.empty}>
          <div style={styles.emptyIcon}>🏐</div>
          <h2 style={styles.emptyTitle}>No teams yet</h2>
          <p style={styles.emptyText}>
            Create your first team to start tracking your volleyball matches and player statistics
          </p>
          <button
            onClick={() => setShowCreateModal(true)}
            style={styles.createButton}
          >
            Create Your First Team
          </button>
        </div>
      ) : (
        <div style={styles.grid}>
          {teams.map((team) => (
            <div
              key={team.id}
              style={{
                ...styles.teamCard,
                ...(currentTeam?.id === team.id ? styles.teamCardActive : {})
              }}
            >
              <div style={styles.teamHeader}>
                <div>
                  <h3 style={styles.teamName}>{team.name}</h3>
                  <div style={styles.teamMeta}>
                    <span style={styles.roleBadge}>{team.my_role}</span>
                    <span style={styles.tierBadge}>{team.subscription_tier}</span>
                  </div>
                </div>
                {currentTeam?.id === team.id && (
                  <span style={styles.activeBadge}>Current</span>
                )}
              </div>

              <div style={styles.teamStats}>
                <div style={styles.stat}>
                  <div style={styles.statValue}>{team.matches_this_month}</div>
                  <div style={styles.statLabel}>Matches this month</div>
                </div>
                <div style={styles.stat}>
                  <div style={styles.statValue}>{team.subscription_limit}</div>
                  <div style={styles.statLabel}>Monthly limit</div>
                </div>
              </div>

              <div style={styles.teamActions}>
                <button
                  onClick={() => navigate(`/teams/${team.id}`)}
                  style={styles.viewButton}
                >
                  View Dashboard
                </button>
                {currentTeam?.id !== team.id && (
                  <button
                    onClick={() => handleSwitchTeam(team)}
                    style={styles.switchButton}
                  >
                    Switch to Team
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Team Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Create New Team</h2>
            <form onSubmit={handleCreateTeam}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Team Name</label>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  placeholder="e.g., Springfield Spikers"
                  style={styles.input}
                  autoFocus
                  required
                />
              </div>

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  style={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  style={{
                    ...styles.submitButton,
                    ...(creating ? styles.submitButtonDisabled : {})
                  }}
                >
                  {creating ? 'Creating...' : 'Create Team'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
    margin: 0,
  },
  createButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.2s',
  },
  loading: {
    textAlign: 'center',
    padding: '60px 20px',
    fontSize: '18px',
    color: '#666',
  },
  empty: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '24px',
    color: '#333',
    marginBottom: '12px',
  },
  emptyText: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '32px',
    maxWidth: '500px',
    margin: '0 auto 32px auto',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
  },
  teamCard: {
    background: 'white',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    padding: '24px',
    transition: 'all 0.2s',
    cursor: 'pointer',
  },
  teamCardActive: {
    borderColor: '#667eea',
    boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
  },
  teamHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  teamName: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    margin: '0 0 8px 0',
  },
  teamMeta: {
    display: 'flex',
    gap: '8px',
  },
  roleBadge: {
    padding: '4px 12px',
    background: '#f3f4f6',
    color: '#666',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  tierBadge: {
    padding: '4px 12px',
    background: '#dbeafe',
    color: '#1e40af',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  activeBadge: {
    padding: '4px 12px',
    background: '#10b981',
    color: 'white',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
  },
  teamStats: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    marginBottom: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
  },
  stat: {
    textAlign: 'center',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1e3a8a',
  },
  statLabel: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px',
  },
  teamActions: {
    display: 'flex',
    gap: '8px',
  },
  viewButton: {
    flex: 1,
    padding: '10px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  switchButton: {
    flex: 1,
    padding: '10px',
    background: 'white',
    color: '#667eea',
    border: '2px solid #667eea',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: '24px',
  },
  formGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '12px',
    fontSize: '16px',
    border: '2px solid #e5e7eb',
    borderRadius: '8px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s',
  },
  modalActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  cancelButton: {
    padding: '12px 24px',
    background: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButton: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default TeamsPage;
